import { LineStyle, type IPriceLine, type ISeriesApi } from 'lightweight-charts';
import type { InstructionView } from '@/services/manualTradingApi';
import type { RealtimeSpreadDirection } from '@/services/oppDetectorApi';
import { formatSpreadFundingValue } from '@/lib/chartFormat';
import { instructionStatusChartMarker, isSizeDrivenClose } from '@/lib/instructionDisplay';
import { normalizeSymbolInput } from '@/lib/exchangeInstId';
import { labelToManualTradingExchange } from '@/services/manualTradingExchange';

export const INSTRUCTIONS_CHANGED_EVENT = 'octopus:instructions-changed';

export type InstructionThresholdLine = {
  instructionId: string;
  action: 'Open' | 'Close';
  threshold: number;
  thresholdMode: InstructionView['threshold_mode'];
  status: string;
  targetNotional: number;
  executedNotional: number;
  perOrderNotional: number;
  validity: InstructionView['validity'];
  isSizeDrivenClose: boolean;
};

export type InstructionExchangePairQuery = {
  symbol: string;
  exchange1: string;
  exchange2: string;
};

type TrackedPriceLine = {
  line: IPriceLine;
  series: ISeriesApi<'Line'>;
};

/** List instructions by symbol + two exchanges (order-independent). */
export function buildInstructionExchangePairQuery(
  symbol: string,
  exchange1: string,
  exchange2: string,
): InstructionExchangePairQuery | null {
  const normalizedSymbol = normalizeSymbolInput(symbol);
  const ex1 = labelToManualTradingExchange(exchange1);
  const ex2 = labelToManualTradingExchange(exchange2);
  if (!normalizedSymbol || !ex1 || !ex2 || ex1 === ex2) return null;
  return {
    symbol: normalizedSymbol,
    exchange1: ex1,
    exchange2: ex2,
  };
}

export function chartSpreadLongShortExchanges(
  exchange1: string,
  exchange2: string,
  direction: RealtimeSpreadDirection,
): { longExchange: string; shortExchange: string } | null {
  const longExchange = labelToManualTradingExchange(
    direction === 'long1_short2' ? exchange1 : exchange2,
  );
  const shortExchange = labelToManualTradingExchange(
    direction === 'long1_short2' ? exchange2 : exchange1,
  );
  if (!longExchange || !shortExchange) return null;
  return { longExchange, shortExchange };
}

/** Instruction long/short must match the chart's current spread direction. */
export function instructionMatchesChartSpreadDirection(
  instruction: InstructionView,
  exchange1: string,
  exchange2: string,
  direction: RealtimeSpreadDirection,
): boolean {
  const legs = chartSpreadLongShortExchanges(exchange1, exchange2, direction);
  if (!legs) return false;
  return (
    instruction.long_exchange === legs.longExchange
    && instruction.short_exchange === legs.shortExchange
  );
}

/** Match instructions to the current chart pair (symbol normalized, exchanges allow leg swap). */
export function filterInstructionsForChartPair(
  instructions: InstructionView[],
  symbol: string,
  exchange1: string,
  exchange2: string,
): InstructionView[] {
  const chartSymbol = normalizeSymbolInput(symbol);
  if (!chartSymbol) return [];

  const ex1 = labelToManualTradingExchange(exchange1);
  const ex2 = labelToManualTradingExchange(exchange2);
  if (!ex1 || !ex2) return [];

  return instructions.filter((row) => {
    if (normalizeSymbolInput(row.symbol) !== chartSymbol) return false;
    return (
      (row.long_exchange === ex1 && row.short_exchange === ex2)
      || (row.long_exchange === ex2 && row.short_exchange === ex1)
    );
  });
}

export function filterInstructionsForChartSpreadDirection(
  instructions: InstructionView[],
  exchange1: string,
  exchange2: string,
  direction: RealtimeSpreadDirection,
): InstructionView[] {
  return instructions.filter((row) =>
    instructionMatchesChartSpreadDirection(row, exchange1, exchange2, direction),
  );
}

/** Active instructions with a finite spread threshold (skip Immediate). */
export function selectActiveInstructionThresholds(
  instructions: InstructionView[],
): InstructionThresholdLine[] {
  return instructions
    .filter((row) => {
      if (row.status !== 'Running' && row.status !== 'Paused') return false;
      if (row.threshold_mode === 'Immediate') return false;
      if (row.threshold == null || !Number.isFinite(row.threshold)) return false;
      return true;
    })
    .map((row) => ({
      instructionId: row.instruction_id,
      action: row.action,
      threshold: row.threshold!,
      thresholdMode: row.threshold_mode,
      status: row.status,
      targetNotional: row.target_notional,
      executedNotional: row.executed_notional,
      perOrderNotional: row.per_order_notional,
      validity: row.validity,
      isSizeDrivenClose: isSizeDrivenClose(row),
    }));
}

export function notifyInstructionsChanged(): void {
  window.dispatchEvent(new Event(INSTRUCTIONS_CHANGED_EVENT));
}

/** Backend threshold is raw spread ratio; chart Y uses ratio * scale (scale 100 in full mode). */
export function instructionThresholdToChartPrice(threshold: number, chartSpreadScale: number): number {
  return threshold * chartSpreadScale;
}

function thresholdLineNotionalLabel(item: InstructionThresholdLine): string | null {
  if (item.isSizeDrivenClose) return null;
  if (item.action === 'Close' && item.validity === 'persistent') {
    if (!Number.isFinite(item.perOrderNotional) || item.perOrderNotional <= 0) return null;
    return `$${item.perOrderNotional.toFixed(0)}/单`;
  }
  if (!Number.isFinite(item.targetNotional)) return null;
  const executed = Number.isFinite(item.executedNotional) ? item.executedNotional : 0;
  return `$${executed.toFixed(0)}/${item.targetNotional.toFixed(0)}`;
}

export function formatInstructionThresholdAxisCompactLabel(
  item: InstructionThresholdLine,
  chartPrice: number,
): string {
  const prefix = item.action === 'Open' ? '开阈' : '关阈';
  const mode =
    item.thresholdMode === 'GreaterOrEqual'
      ? '≥'
      : item.thresholdMode === 'LessOrEqual'
        ? '≤'
        : '';
  return `${prefix}${mode} ${formatSpreadFundingValue(chartPrice)}`;
}

export function formatInstructionThresholdLineLabel(
  item: InstructionThresholdLine,
  chartPrice: number,
  options?: { includeStatusMarker?: boolean },
): string {
  const statusMarker = options?.includeStatusMarker === false ? '' : `${instructionStatusChartMarker(item.status)} `;
  const prefix = item.action === 'Open' ? '开阈' : '关阈';
  const mode =
    item.thresholdMode === 'GreaterOrEqual'
      ? '≥'
      : item.thresholdMode === 'LessOrEqual'
        ? '≤'
        : '';
  const spreadPart = `${statusMarker}${prefix}${mode} ${formatSpreadFundingValue(chartPrice)}`;
  const notionalPart = thresholdLineNotionalLabel(item);
  return notionalPart ? `${spreadPart} · ${notionalPart}` : spreadPart;
}

function thresholdLineTitle(item: InstructionThresholdLine, chartPrice: number): string {
  return formatInstructionThresholdLineLabel(item, chartPrice, { includeStatusMarker: true });
}

function instructionThresholdLineStyle(status: string): LineStyle {
  return status === 'Paused' ? LineStyle.Dotted : LineStyle.Dashed;
}

function instructionThresholdLineColor(action: InstructionThresholdLine['action'], status: string): string {
  void status;
  return action === 'Open' ? '#16a34a' : '#9333ea';
}

export function clearInstructionThresholdPriceLines(
  trackedLinesRef: { current: TrackedPriceLine[] },
): void {
  for (const tracked of trackedLinesRef.current) {
    try {
      tracked.series.removePriceLine(tracked.line);
    } catch {
      // series/chart may already be destroyed
    }
  }
  trackedLinesRef.current = [];
}

export function syncInstructionThresholdPriceLines(
  openSeries: ISeriesApi<'Line'> | null,
  closeSeries: ISeriesApi<'Line'> | null,
  lines: InstructionThresholdLine[],
  chartSpreadScale: number,
  trackedLinesRef: { current: TrackedPriceLine[] },
): void {
  clearInstructionThresholdPriceLines(trackedLinesRef);

  for (const item of lines) {
    const series = item.action === 'Open' ? openSeries : closeSeries;
    if (!series) continue;

    const chartPrice = instructionThresholdToChartPrice(item.threshold, chartSpreadScale);
    try {
      const line = series.createPriceLine({
        price: chartPrice,
        color: instructionThresholdLineColor(item.action, item.status),
        lineWidth: 2,
        lineStyle: instructionThresholdLineStyle(item.status),
        axisLabelVisible: false,
        title: thresholdLineTitle(item, chartPrice),
      });
      trackedLinesRef.current.push({ line, series });
    } catch {
      // ignore draw errors for a single instruction line
    }
  }
}

/** Re-apply threshold lines after chart data refresh (setData can drop price lines). */
export function reapplyInstructionThresholdPriceLines(
  openSeries: ISeriesApi<'Line'> | null,
  closeSeries: ISeriesApi<'Line'> | null,
  lines: InstructionThresholdLine[],
  chartSpreadScale: number,
  trackedLinesRef: { current: TrackedPriceLine[] },
): void {
  syncInstructionThresholdPriceLines(openSeries, closeSeries, lines, chartSpreadScale, trackedLinesRef);
}
